/**
 * Ordenação client-side das linhas do Detalhe eSocial (protótipo).
 */

function cmpText(a, b) {
  return String(a || "").localeCompare(String(b || ""), "pt-BR", { sensitivity: "base" });
}

function cmpNum(a, b) {
  return (Number(a) || 0) - (Number(b) || 0);
}

function cmpCompetencia(a, b) {
  return cmpText(String(a?.competencia || "").slice(0, 7), String(b?.competencia || "").slice(0, 7));
}

function cmpCodigo(a, b) {
  const na = parseInt(String(a?.codigoRubrica || ""), 10);
  const nb = parseInt(String(b?.codigoRubrica || ""), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return cmpText(a?.codigoRubrica, b?.codigoRubrica);
}

function ordemBool(v) {
  return v ? 0 : 1;
}

/**
 * @param {Array<object>} linhas
 * @param {{ col: string, dir: 'asc'|'desc' }} ordenacao
 */
export function ordenarLinhasEsocialAgrupado(linhas, ordenacao = { col: "competencia", dir: "asc" }) {
  const lista = Array.isArray(linhas) ? linhas.slice() : [];
  const m = ordenacao?.dir === "desc" ? -1 : 1;
  const col = ordenacao?.col || "competencia";

  lista.sort((a, b) => {
    let p = 0;
    switch (col) {
      case "competencia":
        p = cmpCompetencia(a, b);
        break;
      case "codigoRubrica":
        p = cmpCodigo(a, b);
        break;
      case "descricaoRubrica":
        p = cmpText(a.descricaoRubrica, b.descricaoRubrica);
        break;
      case "tipoRubrica":
        p = cmpText(a.tipoRubrica, b.tipoRubrica);
        break;
      case "incidenciaRubrica":
        p = cmpText(a.incidenciaRubrica, b.incidenciaRubrica);
        break;
      case "qtdLancamentos":
        p = cmpNum(a.qtdLancamentos, b.qtdLancamentos);
        break;
      case "valorBase":
        p = cmpNum(a.valorBase, b.valorBase);
        break;
      case "valorInss":
        p = cmpNum(a.valorInss, b.valorInss);
        break;
      case "valorTerceiros":
        p = cmpNum(a.valorTerceiros, b.valorTerceiros);
        break;
      case "valorRat":
        p = cmpNum(a.valorRat, b.valorRat);
        break;
      case "valorTotal":
        p = cmpNum(a.valorTotal, b.valorTotal);
        break;
      case "valorInssSelic":
        p = cmpNum(a.valorInssSelic, b.valorInssSelic);
        break;
      case "valorTerceirosSelic":
        p = cmpNum(a.valorTerceirosSelic, b.valorTerceirosSelic);
        break;
      case "valorRatSelic":
        p = cmpNum(a.valorRatSelic, b.valorRatSelic);
        break;
      case "valorTotalSelic":
        p = cmpNum(a.valorTotalSelic, b.valorTotalSelic);
        break;
      case "aliqSelic":
        p = cmpNum(a.aliqSelic, b.aliqSelic);
        break;
      case "valorSelic":
        p = cmpNum(a.valorSelic, b.valorSelic);
        break;
      case "flagIncidencia":
        p = ordemBool(a.flagIncidencia) - ordemBool(b.flagIncidencia);
        break;
      case "baseLegal":
        // Ordena pelo texto; rubricas sem base legal ficam juntas no início.
        p = String(a.baseLegalDescricao || "").localeCompare(
          String(b.baseLegalDescricao || ""),
          "pt-BR"
        );
        break;
      case "verbaFerias":
        p = ordemBool(a.verbaFerias) - ordemBool(b.verbaFerias);
        break;
      default:
        p = 0;
    }
    if (p !== 0) return m * p;
    const c = cmpCompetencia(a, b);
    if (c !== 0) return c;
    return cmpCodigo(a, b);
  });

  return lista;
}

/** Paleta alinhada ao card SPED (ano), com teal eSocial no primeiro tom. */
const CORES_ANO = ["#1f7a8c", "#2a8f6f", "#c45c26", "#6b4c9a", "#b8860b"];

/** Paleta cíclica para o card "Totais por Base Legal" — nº de bases é dinâmico. */
const CORES_BASE_LEGAL = ["#2563eb", "#0e9f6e", "#d97706", "#7c3aed", "#dc2626", "#0891b2"];
const COR_SEM_BASE_LEGAL = "#cbd5e1";

/** Cores fixas do card "Totais por Tipo de Base Legal" (TXM-480) — mesma classificação
 * usada no rodapé do Resumo Geral exportado (ver api-relatorios-sumarizacao/resumogeral.go). */
const COR_DESNECESSIDADE = "#64748b";
const COR_SUGESTAO = "#2563eb";

function r2(x) {
  return Math.round(Number(x) * 100) / 100;
}

function anoDeCompetencia(competencia) {
  if (!competencia || String(competencia).length < 4) return null;
  const y = parseInt(String(competencia).slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

/**
 * Agrega `valorTotalSelic` por chave arbitrária, preservando a ordem de 1ª aparição
 * (usado tanto por Base Legal quanto por Tipo de Base Legal).
 */
function agregarPorChave(lista, resolverChaveRotulo) {
  const map = new Map();
  for (const r of lista) {
    const { chave, rotulo } = resolverChaveRotulo(r);
    if (!map.has(chave)) map.set(chave, { chave, rotulo, valor: 0 });
    map.get(chave).valor += Number(r.valorTotalSelic) || 0;
  }
  return Array.from(map.values()).map((g) => ({ ...g, valor: r2(g.valor) }));
}

/**
 * Totais e série por ano civil a partir das linhas filtradas
 * (mesmo padrão do card maior do Detalhe SPED), mais os agrupamentos por Base Legal
 * e por Tipo de Base Legal (TXM-480) — os dois últimos precisam do catálogo de bases
 * legais só para resolver `medidaJudicial` a partir do `baseLegalId` de cada linha.
 * @param {Array<object>} linhas
 * @param {Array<{id: number, medidaJudicial: string}>} [basesLegais]
 */
export function agregarDashboardEsocial(linhas, basesLegais = []) {
  const lista = Array.isArray(linhas) ? linhas : [];
  const map = new Map();
  const rubricas = new Set();
  let totalInssSelic = 0;
  let totalTerceirosSelic = 0;
  let totalRatSelic = 0;
  let totalSelicApurado = 0;
  let valorSelic = 0;

  for (const r of lista) {
    totalInssSelic += Number(r.valorInssSelic) || 0;
    totalTerceirosSelic += Number(r.valorTerceirosSelic) || 0;
    totalRatSelic += Number(r.valorRatSelic) || 0;
    totalSelicApurado += Number(r.valorTotalSelic) || 0;
    valorSelic += Number(r.valorSelic) || 0;
    if (r.codigoRubrica != null) rubricas.add(String(r.codigoRubrica));

    const ano = anoDeCompetencia(r.competencia);
    if (ano == null) continue;
    if (!map.has(ano)) {
      map.set(ano, {
        ano,
        valorInssSelic: 0,
        valorTerceirosSelic: 0,
        valorRatSelic: 0,
        valorTotalSelic: 0,
        valorSelic: 0,
      });
    }
    const bucket = map.get(ano);
    bucket.valorInssSelic += Number(r.valorInssSelic) || 0;
    bucket.valorTerceirosSelic += Number(r.valorTerceirosSelic) || 0;
    bucket.valorRatSelic += Number(r.valorRatSelic) || 0;
    bucket.valorTotalSelic += Number(r.valorTotalSelic) || 0;
    bucket.valorSelic += Number(r.valorSelic) || 0;
  }

  const serie = Array.from(map.values())
    .sort((a, b) => a.ano - b.ano)
    .map((item, idx) => ({
      ...item,
      valorInssSelic: r2(item.valorInssSelic),
      valorTerceirosSelic: r2(item.valorTerceirosSelic),
      valorRatSelic: r2(item.valorRatSelic),
      valorTotalSelic: r2(item.valorTotalSelic),
      valorSelic: r2(item.valorSelic),
      cor: CORES_ANO[idx % CORES_ANO.length],
    }));

  const medidaPorBaseLegalId = new Map(
    (basesLegais || []).map((b) => [b.id, b.medidaJudicial])
  );

  const serieBaseLegal = agregarPorChave(lista, (r) =>
    r.baseLegalId == null
      ? { chave: "sem", rotulo: "Sem base legal" }
      : { chave: String(r.baseLegalId), rotulo: r.baseLegalDescricao || `Base Legal #${r.baseLegalId}` }
  ).map((g, idx) => ({
    ...g,
    cor: g.chave === "sem" ? COR_SEM_BASE_LEGAL : CORES_BASE_LEGAL[idx % CORES_BASE_LEGAL.length],
  }));

  const serieTipoOrdenada = agregarPorChave(lista, (r) => {
    if (r.baseLegalId == null) return { chave: "sem", rotulo: "Sem base legal" };
    const medida = medidaPorBaseLegalId.get(r.baseLegalId);
    return medida === "sugestao"
      ? { chave: "sugestao", rotulo: "Sugestão de medida judicial" }
      : { chave: "desnecessidade", rotulo: "Desnecessidade de medida judicial" };
  });
  const ORDEM_TIPO = ["desnecessidade", "sugestao", "sem"];
  const CORES_TIPO = { desnecessidade: COR_DESNECESSIDADE, sugestao: COR_SUGESTAO, sem: COR_SEM_BASE_LEGAL };
  const serieTipo = ORDEM_TIPO.map((chave) => serieTipoOrdenada.find((g) => g.chave === chave))
    .filter(Boolean)
    .map((g) => ({ ...g, cor: CORES_TIPO[g.chave] }));

  return {
    cards: {
      totalInssSelic: r2(totalInssSelic),
      totalTerceirosSelic: r2(totalTerceirosSelic),
      totalRatSelic: r2(totalRatSelic),
      totalSelicApurado: r2(totalSelicApurado),
      valorSelic: r2(valorSelic),
      totalRubricas: rubricas.size,
    },
    serie,
    serieBaseLegal,
    serieTipo,
  };
}
